import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { clean } from "../utils/utils";
import { EventTagService } from "../services/EventTagService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { EventTag } from "../models/EventTag";
import { MatterService } from "../services/MatterService";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface EventTagContextProps {
  eventTags: EventTag[];
  loading: Loadings;
  error: Errors;
  createEventTag: (
    data: Omit<EventTag, "_id" | "matterId">,
  ) => Promise<EventTag>;
  updateEventTag: (id: string, data: Partial<EventTag>) => Promise<void>;
  deleteEventTag: (id: string) => Promise<void>;
}

const EventTagContext = createContext<EventTagContextProps | undefined>(
  undefined,
);

export const useEventTag = (): EventTagContextProps => {
  const context = useContext(EventTagContext);
  if (!context) {
    throw new Error("useEventTag must be used within an EventTagProvider");
  }
  return context;
};

export const EventTagProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const [eventTags, setEventTags] = useState<EventTag[]>([]);
  const [loading, setLoading] = useState<Loadings>({
    list: false,
    details: false,
  });
  const [error, setError] = useState<Errors>({});

  const fetchAll = async () => {
    if (!user || !currentMatterId) return;
    setError({});

    try {
      const tags = await MatterService.getEventTags(currentMatterId);
      console.log("Fetched event tags:", tags.length);
      setEventTags(tags);
    } catch (err: any) {
      setError({ list: err, details: err });
      console.error("Failed to load event types or subtypes:", err);
    } finally {
      setLoading({ list: false, details: false });
    }
  };

  useEffect(() => {
    setLoading({ list: true, details: true });
    fetchAll();
  }, [user, currentMatterId]);

  useSocketEventByIds("eventtags:changed", ["*"], fetchAll);

  const createEventTag = async (
    data: Omit<EventTag, "_id" | "matterId">,
  ): Promise<EventTag> => {
    const tag = await EventTagService.createEventTag({
      ...data,
      matterId: currentMatterId!,
    });
    eventTags.push(tag);
    return tag;
  };

  const updateEventTag = async (id: string, data: Partial<EventTag>) => {
    await EventTagService.updateEventTag(id, clean(data));
  };

  const deleteEventTag = async (id: string) => {
    await EventTagService.deleteEventTag(id);
    setEventTags((prev) => prev.filter((cat) => cat._id !== id));
  };

  return (
    <EventTagContext.Provider
      value={{
        eventTags,
        loading,
        error,
        createEventTag,
        updateEventTag,
        deleteEventTag,
      }}
    >
      {children}
    </EventTagContext.Provider>
  );
};
